import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, input, OnDestroy, output } from '@angular/core';
import { BbitNaturalLanguageDateTimeParser } from '@bbitgmbh/bbit.natural-language-parser';
import { BbitButtonType, BbitI18n, CalendarUtils, IBbitParsedMonth } from '@bbitgmbh/bbit.utils';
import { LuxonPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bbit-month-navigator',
  templateUrl: './month-navigator.component.html',
  styleUrls: ['./month-navigator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, AsyncPipe, LuxonPipe],
})
export class MonthNavigatorComponent implements OnDestroy {
  @Input()
  set date(date: DateTime) {
    this.dateSubject.next(date);
  }
  public readonly dateChange = output();
  readonly timezone = input(BbitI18n.getTimezone());

  public dateSubject = new BehaviorSubject<DateTime | undefined>(undefined);
  public BbitButtonType = BbitButtonType;
  public currentMonth!: number;
  // biome-ignore lint/suspicious/noExplicitAny: required
  public currentMonthSubject: BehaviorSubject<IBbitParsedMonth> = new BehaviorSubject(undefined as any);
  private _ngUnsubscribe = new Subject<void>();
  private _parser = new BbitNaturalLanguageDateTimeParser();

  constructor() {
    this.dateSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe((date) => {
      this.currentMonthSubject.next(CalendarUtils.parseMonth(date));
    });
    this._parser.loadBuildInGrammars();
  }

  public ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  public nextMonth(): void {
    this.currentMonthSubject.next(CalendarUtils.getNextMonth(this.currentMonthSubject.value));
  }

  public previousMonth(): void {
    this.currentMonthSubject.next(CalendarUtils.getPreviousMonth(this.currentMonthSubject.value));
  }

  public select(date: DateTime): void {
    const newDate = date.set({
      hour: this.dateSubject.value?.hour,
      minute: this.dateSubject.value?.minute,
      second: this.dateSubject.value?.second,
    });
    this.dateSubject.next(newDate);
    // biome-ignore lint/suspicious/noExplicitAny: required
    this.dateChange.emit(this.dateSubject.value as any);
  }

  public isDateSelected(date: DateTime): boolean {
    return CalendarUtils.areDatesEqual(date, this.dateSubject.value);
  }
}
